import "./ClubStep1.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Row } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppContainer from "../../../../../commons/containers/AppContainer";
import AppCurrencyInputV2 from "../../../../../commons/inputs/AppCurrencyInputV2";
import AppInputV2 from "../../../../../commons/inputs/AppInputV2";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppTextAreaV2 from "../../../../../commons/inputs/AppTextAreaV2";
import HallAppToast from "../../../../../commons/HallAppToast";

const { Formik } = formik;

const FREQUENCIES = [
  { value: "MONTHLY", label: "Mensal", hint: "Cobrança todo mês" },
  { value: "QUARTERLY", label: "Trimestral", hint: "Cobrança a cada 3 meses" },
  { value: "YEARLY", label: "Anual", hint: "Cobrança uma vez por ano" },
];

const BILLING_RULES = [
  {
    value: "ON_SUBSCRIPTION_DAY",
    label: "No dia da adesão",
    hint: "Renova no mesmo dia do mês que o cliente assinou",
  },
  {
    value: "FIXED_DAY_OF_MONTH",
    label: "Dia fixo do mês",
    hint: "Ex: todo dia 5 (você escolhe o dia)",
  },
];

function FrequencyCard({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="club-step1__option-card w-100"
      data-active={active ? "1" : "0"}
    >
      <div className="club-step1__option-title">{item.label}</div>
      <div className="club-step1__option-hint">{item.hint}</div>
    </button>
  );
}

function ClubStep1({ nextStep, data }) {
  const initialValues = useMemo(
    () => ({
      name: data?.name ?? "",
      shortDescription: data?.shortDescription ?? "",
      fullDescription: data?.fullDescription ?? "",

      currency: data?.currency ?? "BRL",
      frequency: data?.frequency ?? "MONTHLY",
      billingRule: data?.billingRule ?? "ON_SUBSCRIPTION_DAY",
      fixedDayOfMonth: data?.fixedDayOfMonth ?? "",

      limitSubscribersEnabled: data?.limitSubscribers != null,
      limitSubscribers: data?.limitSubscribers ?? "",

      allowMultipleSubscriptionsPerCustomer: !!data?.allowMultipleSubscriptionsPerCustomer,

      visibleOnSite: data?.visibleOnSite !== false, // default true
      active: data?.active !== false, // default true

      displayOrder: data?.displayOrder ?? "",
    }),
    [data]
  );

  // ✅ currency input state (do jeito que você pediu)
  const [value, setValue] = useState(Number(data?.amount ?? 0));

  // quando abrir edição e data mudar, atualiza value
  useEffect(() => {
    setValue(Number(data?.amount ?? 0));
  }, [data?.amount]);

  const schema = yup.object().shape({
    name: yup.string().trim().required("Nome do clube é obrigatório").min(3, "Mínimo de 3 caracteres"),

    fixedDayOfMonth: yup.string().when("billingRule", {
      is: "FIXED_DAY_OF_MONTH",
      then: (s) =>
        s
          .required("Escolha o dia do mês")
          .matches(/^(0?[1-9]|[12]\d|3[01])$/, "Dia inválido (1 a 31)"),
      otherwise: (s) => s.notRequired().nullable(true),
    }),

    shortDescription: yup.string().max(120, "Máx. 120 caracteres"),

    displayOrder: yup.string().nullable().test("isNumberOrEmpty", "Ordem deve ser um número", (v) => {
      if (!v) return true;
      return /^\d+$/.test(String(v));
    }),

    limitSubscribers: yup.string().when("limitSubscribersEnabled", {
      is: true,
      then: (s) =>
        s
          .required("Informe o limite")
          .matches(/^\d+$/, "Use apenas números")
          .test("min1", "O limite deve ser no mínimo 1", (v) => Number(v) >= 1),
      otherwise: (s) => s.notRequired().nullable(true),
    }),
  });

  const [selectedFrequency, setSelectedFrequency] = useState(initialValues.frequency);
  const [selectedBillingRule, setSelectedBillingRule] = useState(initialValues.billingRule);

  // quando abrir edição, sincroniza os cards
  useEffect(() => {
    setSelectedFrequency(initialValues.frequency);
    setSelectedBillingRule(initialValues.billingRule);
  }, [initialValues.frequency, initialValues.billingRule]);

  const toNext = (values) => {
    if (!values?.name?.trim()) {
      HallAppToast.error("Preencha o nome do clube.");
      return;
    }

    if (!Number(value || 0)) {
      HallAppToast.error("Informe o valor do plano.");
      return;
    }

    const payload = {
      name: values.name.trim(),
      shortDescription: (values.shortDescription || "").trim(),
      fullDescription: (values.fullDescription || "").trim(),

      amount: Number(value || 0),
      currency: values.currency || "BRL",

      frequency: selectedFrequency,
      billingRule: selectedBillingRule,
      fixedDayOfMonth:
        selectedBillingRule === "FIXED_DAY_OF_MONTH" ? Number(values.fixedDayOfMonth) : null,

      limitSubscribers: values.limitSubscribersEnabled ? Number(values.limitSubscribers) : null,
      allowMultipleSubscriptionsPerCustomer: !!values.allowMultipleSubscriptionsPerCustomer,

      visibleOnSite: !!values.visibleOnSite,
      active: !!values.active,

      displayOrder: values.displayOrder ? Number(values.displayOrder) : null,
      salesChannels: Array.isArray(data?.salesChannels) ? data.salesChannels : [], // se quiser manter
    };

    nextStep(payload);
  };

  return (
    <div className="club-step1" style={{ paddingBottom: 90 }}>
      <Formik validationSchema={schema} initialValues={initialValues} enableReinitialize>
        {({ handleChange, setFieldValue, values, touched, errors }) => (
          <Form>
            <div className="mb-3">
              <div className="club-step1__title">Configurar Plano</div>
              <div className="club-step1__subtitle text-muted">
                Nome, preço, recorrência e exibição.
              </div>
            </div>

            {/* Identificação */}
            <div className="club-step1__card">
              <div className="club-step1__card-title">Identificação</div>

              <Row className="g-2">
                <Col xs={12}>
                  <AppInputV2
                    label="Nome do clube"
                    name="name"
                    isRequired
                    errors={errors}
                    touched={touched}
                    value={values.name}
                    placeholder="Ex: Clube VIP Mensal"
                    onChange={handleChange}
                  />
                </Col>

                <Col xs={12}>
                  <AppInputV2
                    label="Descrição curta (opcional)"
                    name="shortDescription"
                    errors={errors}
                    touched={touched}
                    value={values.shortDescription}
                    placeholder="Ex: Desconto + prioridade na agenda"
                    onChange={handleChange}
                  />
                  <div className="club-step1__hint-mini text-muted">Até 120 caracteres.</div>
                </Col>

                <Col xs={12}>
                  <AppTextAreaV2
                    label="Descrição completa (opcional)"
                    name="fullDescription"
                    errors={errors}
                    touched={touched}
                    value={values.fullDescription}
                    placeholder="Explique regras e benefícios com mais detalhes…"
                    onChange={handleChange}
                  />
                </Col>
              </Row>
            </div>

            {/* Preço */}
            <div className="club-step1__card">
              <div className="club-step1__card-title">Preço</div>

              <Row className="g-2">
                <Col xs={12}>
                  <AppCurrencyInputV2
                    label="Valor"
                    isRequired
                    value={value}
                    onChange={(_, v) => setValue(v)}
                  />
                </Col>
              </Row>
            </div>

            {/* Recorrência */}
            <div className="club-step1__card">
              <div className="club-step1__card-title">Recorrência</div>

              <Row className="g-2">
                {FREQUENCIES.map((f) => (
                  <Col xs={6} md={4} key={f.value}>
                    <FrequencyCard
                      item={f}
                      active={selectedFrequency === f.value}
                      onClick={() => {
                        setSelectedFrequency(f.value);
                        setFieldValue("frequency", f.value);
                      }}
                    />
                  </Col>
                ))}
              </Row>

              {selectedBillingRule === "FIXED_DAY_OF_MONTH" && (
                <div className="mt-3">
                  <Row className="g-2">
                    <Col xs={12} sm={6}>
                      <AppInputV2
                        label="Dia do mês (1 a 31)"
                        name="fixedDayOfMonth"
                        isRequired
                        errors={errors}
                        touched={touched}
                        value={values.fixedDayOfMonth}
                        placeholder="Ex: 5"
                        onChange={handleChange}
                        type="number"
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            {/* Exibição */}
            <div className="club-step1__card">
              <div className="club-step1__card-title">Exibição e regras básicas</div>

              <Row className="g-2">
                <Col xs={12} sm={6}>
                  <Form.Check
                    type="switch"
                    id="club-active"
                    label="Clube ativo"
                    checked={!!values.active}
                    onChange={(e) => setFieldValue("active", e.target.checked)}
                  />
                </Col>

                <Col xs={12} sm={6}>
                  <Form.Check
                    type="switch"
                    id="club-visible"
                    label="Visível no site"
                    checked={!!values.visibleOnSite}
                    onChange={(e) => setFieldValue("visibleOnSite", e.target.checked)}
                  />
                </Col>

                <Col xs={12}>
                  <Form.Check
                    type="switch"
                    id="club-limit-enabled"
                    label="Definir limite de assinantes"
                    checked={!!values.limitSubscribersEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setFieldValue("limitSubscribersEnabled", enabled);
                      if (!enabled) setFieldValue("limitSubscribers", "");
                    }}
                  />
                </Col>

                {values.limitSubscribersEnabled && (
                  <Col xs={12} sm={6}>
                    <AppInputV2
                      label="Limite de assinantes"
                      name="limitSubscribers"
                      isRequired
                      errors={errors}
                      touched={touched}
                      value={values.limitSubscribers}
                      placeholder="Ex: 200"
                      onChange={handleChange}
                      type="number"
                    />
                  </Col>
                )}
              </Row>
            </div>

            <div className="club-step1__footer">
              <AppContainer>
                <AppLinkButton name="Próximo" fullWidth={true} onClick={() => toNext(values)} />
              </AppContainer>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ClubStep1;
